import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NbButton, NbButtonModule, NbCardModule, NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { DrbState, DrbStateModel, LoadDrbByVersion, LoadDrbVersions } from 'src/app/features/drbs/redux';
import { Content, PayloadRecord } from 'src/app/models/content';
import { Drb } from 'src/app/models/drb-models/drb';
import { BUNDLE_NAMES, ENVIRONMENTS, PAYLOAD_TYPES } from 'src/app/shared/constants/general-constants';
import { SharedModule } from 'src/app/shared/shared.module';
import { PreviewContentComponent } from 'src/app/shared/ui/preview-content/preview-content.component';
import { ENVIRONMENT } from 'src/environments/environment';
import { ContentState } from '../../redux';

@Component({
  selector: 'app-view-content',
  templateUrl: './view-content.component.html',
  styleUrls: ['./view-content.component.css'],
  standalone: true,
  imports: [SharedModule, NbCardModule, NbButtonModule]
})
export class ViewContentComponent implements OnInit, OnDestroy {
  public content$: Observable<Content[] | undefined> = inject(Store).select(ContentState.content);
  public version$: Observable<Record<string, string[]>> = inject(Store).select(DrbState.versions);
  public marketing$: Observable<Drb | undefined> = inject(Store).select(DrbState.marketing);
  public marketing_translations$: Observable<Drb | undefined> = inject(Store).select(DrbState.marketing_translations);
  private unsubscribe = new Subject<void>();

  public content!: Content;

  public localeDisplays: string[] = [];

  public marketing?: Drb = undefined;
  public marketing_translations?: Drb = undefined;

  constructor(
    protected ref: NbDialogRef<ViewContentComponent>,
    private dialogService: NbDialogService,
    private store: Store
  ) {
    if (![ENVIRONMENTS.TKSTAGE, ENVIRONMENTS.TKPROD].includes(ENVIRONMENT.name as any)) {
      Object.values(BUNDLE_NAMES).forEach((bundle_name) => {
        if (this.store.selectSnapshot(DrbState.versions)[bundle_name].length === 0) {
          this.store.dispatch(new LoadDrbVersions(bundle_name));
        }
      });
    }
  }

  public ngOnInit() {
    this.marketing$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.marketing = data));
    this.marketing_translations$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data) => (this.marketing_translations = data));
    this.version$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (![ENVIRONMENTS.TKSTAGE, ENVIRONMENTS.TKPROD].includes(ENVIRONMENT.name as any)) {
        Object.values(BUNDLE_NAMES).forEach((bundle_name) => {
          if (data[bundle_name].length > 0) {
            const versions = [...data[bundle_name]].reverse();
            if (!this.store.selectSnapshot(DrbState.getByKey(bundle_name as keyof DrbStateModel))) {
              this.store.dispatch(new LoadDrbByVersion(versions[0], bundle_name));
            }
          }
        });
      }
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public get payloadRecords(): PayloadRecord[] {
    return this.content.payload_records ?? [];
  }

  public get multiPageRecords(): PayloadRecord[] {
    return this.payloadRecords.filter((r) => r.payload_record_type === PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG);
  }

  public get hasMultiPageRecords(): boolean {
    return this.multiPageRecords.length > 0;
  }

  public get canPreviewContent(): boolean {
    return this.payloadRecords.some((r) => r.payload_record_type !== PAYLOAD_TYPES.MULTI_PAGE_CONTEXTUAL_MSG);
  }

  public close() {
    this.ref.close();
  }

  public previewContent() {
    this.dialogService.open(PreviewContentComponent, {
      closeOnEsc: true,
      closeOnBackdropClick: true,
      context: { content: this.content }
    });
  }
}
